import dayjs from 'dayjs';

import {
  GetAdminProjectResponse,
  LicenseType,
} from 'src/project/types/projects';
import { SitelifeFile, FileType } from 'src/file/types';

export type ProjectFileType = '2D' | '3D' | '2D/3D' | 'empty' | 'N/A';

/**
 * Determines the file types used by a project based on its files
 * @param project - The project to analyze
 * @param files - The files associated with the project (can be undefined if not loaded)
 * @returns The file type classification
 */
export function calculateProjectFileType(
  project: GetAdminProjectResponse,
  files?: SitelifeFile[],
): ProjectFileType {
  // For inactive projects, return N/A
  const projectStatus = determineProjectStatus(project);
  if (projectStatus !== 'ACTIVE') {
    return 'N/A';
  }

  // If files haven't been loaded yet, return empty
  if (!files) {
    return 'empty';
  }

  const has2DFiles = files.some((file) => file.type === FileType.PDF_PLAN);
  const has3DFiles = files.some(
    (file) => file.type === FileType.REVIT || file.type === FileType.IFC,
  );

  if (has2DFiles && has3DFiles) {
    return '2D/3D';
  } else if (has2DFiles) {
    return '2D';
  } else if (has3DFiles) {
    return '3D';
  } else {
    return 'empty';
  }
}

/**
 * Normalizes file type values for filtering
 * Maps compound types to individual types for filter matching
 * @param fileType - The calculated file type
 * @returns Array of filter-compatible values
 */
export function normalizeFileTypeForFilter(
  fileType: ProjectFileType,
): string[] {
  switch (fileType) {
    case '2D/3D':
      return ['2D', '3D'];
    case '2D':
      return ['2D'];
    case '3D':
      return ['3D'];
    case 'empty':
      return ['empty'];
    case 'N/A':
      return []; // N/A files are not filterable
    default:
      return [];
  }
}

/**
 * Determines project status (extracted from ProjectRow logic)
 * @param project - The project to analyze
 * @returns 'ACTIVE' or 'ARCHIVED'
 */
function determineProjectStatus(
  project: GetAdminProjectResponse,
): 'ACTIVE' | 'ARCHIVED' {
  const licenseType = project.licenseType || project.license?.type;
  const expirationDate = project.license?.expirationDate;

  // FREE and BASIC licenses: check expiration date
  if (
    (licenseType === LicenseType.FREE || licenseType === LicenseType.BASIC) &&
    expirationDate
  ) {
    const isExpired = dayjs(expirationDate)
      .startOf('day')
      .isBefore(dayjs().startOf('day'));
    return isExpired ? 'ARCHIVED' : 'ACTIVE';
  }

  return 'ACTIVE'; // Default for DEMO and other license types
}

/**
 * Checks if a project matches the selected file type filters
 * @param project - The project to check
 * @param files - The files associated with the project
 * @param selectedFileTypes - The selected filter values
 * @returns Whether the project matches the filter
 */
export function projectMatchesFileTypeFilter(
  project: GetAdminProjectResponse,
  files: SitelifeFile[] | undefined,
  selectedFileTypes: string[],
): boolean {
  // If no file type filters are selected, show all projects
  if (selectedFileTypes.length === 0) {
    return true;
  }

  const projectFileType = calculateProjectFileType(project, files);
  const normalizedTypes = normalizeFileTypeForFilter(projectFileType);

  // Check if any of the project's file types match the selected filters
  return normalizedTypes.some((type) => selectedFileTypes.includes(type));
}
